  (() => {
    const requests = new Map();
    const keys = {
      zone: ['hardiness-zone', 'usda-zone', 'usda_zone', 'usda-hardiness-zone'],
      sunlight: ['sunlight'],
      water: ['watering-needs'],
      space: ['suitable-space', 'growing-space'],
      characteristics: ['plant-characteristics']
    };
    const normalize = value => String(value).trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const findGroup = (filters, key) => filters.find(filter => keys[key].some(suffix => filter.param.endsWith('.' + suffix)));
    const findValues = (filter, labels) => filter ? filter.values.filter(value => Number(value.count) > 0 && labels.includes(normalize(value.label))) : [];
    const destination = (base, values) => {
      const url = new URL(base, location.origin);
      url.search = '';
      url.hash = '';
      values.forEach(value => url.searchParams.append(value.param, value.value));
      return url.pathname + url.search;
    };
    const readData = element => {
      const script = element.querySelector('[data-sw-need-filter-data]');
      return script ? JSON.parse(script.textContent) : null;
    };
    const fetchData = (url, signal) => fetch(url, { credentials: 'same-origin', signal }).then(response => {
      if (!response.ok) throw new Error('Filter data unavailable');
      return response.text();
    }).then(html => {
      const data = readData(new DOMParser().parseFromString(html, 'text/html'));
      if (!data || !data.filters.length) throw new Error('Filter data unavailable');
      return data;
    });
    const getData = (root, embedded) => {
      if (embedded && embedded.filters.length) return Promise.resolve(embedded);
      const url = new URL(root.dataset.catalogUrl, location.origin);
      url.search = '';
      url.hash = '';
      url.searchParams.set('section_id', 'sw-need-filters');
      const cacheKey = url.href;
      if (!requests.has(cacheKey)) {
        requests.set(cacheKey, fetchData(url).catch(error => {
          requests.delete(cacheKey);
          throw error;
        }));
      }
      return requests.get(cacheKey);
    };
    const selectedValues = root => Array.from(root.querySelectorAll('[data-need-select]')).filter(select => select.name && select.value).map(select => ({ param: select.name, value: select.value }));
    const hydrate = (root, data, refresh = false) => {
      const filters = data.filters || [];
      const groups = Object.fromEntries(Object.keys(keys).map(key => [key, findGroup(filters, key)]));
      const quick = {
        full_sun: findValues(groups.sunlight, ['full-sun']),
        shade: findValues(groups.sunlight, ['shade', 'partial-shade']),
        ground_cover: findValues(groups.characteristics, ['ground-cover', 'groundcover']),
        pollinators: findValues(groups.characteristics, ['attracts-pollinators', 'pollinator-friendly']),
        low_water: findValues(groups.water, ['low', 'low-water', 'low-water-needs'])
      };
      if (!refresh) {
        root.querySelectorAll('[data-need]').forEach(link => {
          const values = quick[link.dataset.need];
          if (values && values.length && (link.dataset.need !== 'shade' || values.length === 1 || groups.sunlight.operator === 'OR')) {
            link.href = destination(data.url || root.dataset.catalogUrl, values);
            const count = link.querySelector('[data-need-count]');
            if (count && values.length === 1 && link.dataset.need !== 'shade') {
              count.textContent = root.dataset.countTemplate.replace('__COUNT__', Number(values[0].count).toLocaleString('en-US'));
              count.hidden = false;
            }
          }
        });
      }
      let enabled = 0;
      root.querySelectorAll('[data-need-select]').forEach(select => {
        const key = select.dataset.needSelect;
        const group = groups[key];
        const previousValue = select.value;
        const previousLabel = select.selectedOptions[0].dataset.label || select.selectedOptions[0].textContent;
        while (select.options.length > 1) select.remove(1);
        const values = group ? group.values.filter(value => Number(value.count) > 0 || value.value === previousValue).slice() : [];
        if (previousValue && !values.some(value => value.value === previousValue)) {
          values.push({ label: previousLabel, param: select.name, value: previousValue, count: 0 });
        }
        if (!values.length) {
          select.closest('[data-need-field]').hidden = true;
          return;
        }
        select.closest('[data-need-field]').hidden = false;
        if (group) select.name = group.param;
        values.sort((a, b) => Number(b.count) - Number(a.count) || a.label.localeCompare(b.label, 'en', { sensitivity: 'base' }));
        values.forEach(value => {
          const label = root.dataset.optionTemplate.replace('__LABEL__', value.label).replace('__COUNT__', Number(value.count).toLocaleString('en-US'));
          const option = new Option(label, value.value, false, value.value === previousValue);
          option.dataset.label = value.label;
          select.add(option);
        });
        enabled += 1;
      });
      const fields = root.querySelector('[data-need-fields]');
      const submit = root.querySelector('[data-need-submit]');
      const status = root.querySelector('[data-need-status]');
      if (fields) fields.disabled = enabled === 0;
      if (submit) submit.disabled = enabled === 0;
      if (status) status.hidden = true;
      if (fields) fields.removeAttribute('aria-busy');
      const error = root.querySelector('[data-need-error]');
      if (error) error.hidden = enabled > 0;
      if (enabled === 0 && fields) root.querySelector('[data-need-error]').hidden = false;
      root.dataset.ready = 'true';
    };
    const initialize = root => {
      if (root.dataset.initialized) return;
      root.dataset.initialized = 'true';
      const form = root.querySelector('[data-need-form]');
      if (form) {
        form.addEventListener('submit', event => {
          event.preventDefault();
          if (root.dataset.ready !== 'true') return;
          location.assign(destination(root.dataset.catalogUrl, selectedValues(root)));
        });
        let controller;
        let sequence = 0;
        let timer;
        const refresh = () => {
          if (controller) controller.abort();
          controller = new AbortController();
          const current = ++sequence;
          const url = new URL(destination(root.dataset.catalogUrl, selectedValues(root)), location.origin);
          url.searchParams.set('section_id', 'sw-need-filters');
          const status = root.querySelector('[data-need-status]');
          const fields = root.querySelector('[data-need-fields]');
          const submit = root.querySelector('[data-need-submit]');
          status.textContent = root.dataset.updatingText;
          status.hidden = false;
          fields.setAttribute('aria-busy', 'true');
          submit.disabled = true;
          fetchData(url, controller.signal).then(data => {
            if (current === sequence) hydrate(root, data, true);
          }).catch(error => {
            if (error.name === 'AbortError' || current !== sequence) return;
            status.textContent = root.dataset.updateFailedText;
            status.hidden = false;
            fields.removeAttribute('aria-busy');
            submit.disabled = false;
          });
        };
        form.addEventListener('change', event => {
          if (!event.target.matches('[data-need-select]') || root.dataset.ready !== 'true') return;
          clearTimeout(timer);
          if (controller) controller.abort();
          sequence += 1;
          timer = setTimeout(refresh, 180);
        });
        form.addEventListener('reset', () => {
          clearTimeout(timer);
          if (controller) controller.abort();
          sequence += 1;
          timer = setTimeout(refresh, 0);
        });
      }
      let embedded;
      try { embedded = readData(root); } catch (_) { embedded = null; }
      getData(root, embedded).then(data => hydrate(root, data)).catch(() => {
        const status = root.querySelector('[data-need-status]');
        const error = root.querySelector('[data-need-error]');
        if (status) status.hidden = true;
        if (error) error.hidden = false;
      });
    };
    const initializeAll = container => {
      if (container.matches && container.matches('[data-sw-need]')) initialize(container);
      container.querySelectorAll('[data-sw-need]').forEach(initialize);
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => initializeAll(document), { once: true });
    } else {
      initializeAll(document);
    }
    document.addEventListener('shopify:section:load', event => initializeAll(event.target));
  })();
